/**
 * Order Mission Control - Order Detail View
 *
 * Sci-Fi HUD styled order detail page with:
 * - Power Line: Animated status timeline with SLA telemetry
 * - Customer Intel Radar: VIP/priority detection with visual badges
 * - Holographic Product List: Flight Check mode for packing verification
 * - Glassmorphism Info Modules: Customer, Delivery, Store, Financial data
 *
 * **Action Buttons:**
 * - Delivered orders: Reorder, Order Returned (placeholders)
 * - Pre-delivered orders: Report Issue (placeholder)
 *
 * @module showOrderItem
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Edit,
  useGetRecordId,
  useRecordContext,
  useRefresh,
  useUpdate,
  WithRecord,
} from "react-admin";
import DownloadInvoice from "./downloadInvoice";
import { Download as DownloadIcon } from "@mui/icons-material";
import { printDocument, downloadDocument, runDocumentAction } from "../utils/orderDocuments";
import config from "../config";
import { getStoredToken } from "../services/tokenRefreshService";
import {
  Grid,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  Link,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Avatar,
  keyframes,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StoreIcon from "@mui/icons-material/Store";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import InventoryIcon from "@mui/icons-material/Inventory";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import PrintIcon from "@mui/icons-material/Print";
import NoteIcon from "@mui/icons-material/Note";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ListAltIcon from "@mui/icons-material/ListAlt";

// Mission Control Components
import {
  PowerLine,
  CustomerIntelRadar,
  GlassCard,
  GlassInfoRow,
  GlassDivider,
  HolographicProductList,
  ProofPhotos,
  ChatPanel,
  ChatHistory,
  StatusPipeline,
  useMissionTheme,
} from "./OrderMissionControl";
// The order page shows the same customer profile as the Customers page rather
// than a second, thinner one of its own — one profile, one place to maintain it.
import { CustomerDetailsModal } from "./UserManagement/CustomerDetailsModal";
import { usePermissions } from "../context/PermissionContext";
import { usePickingStatus } from "../hooks/usePickingStatus";
import { useOrderStatusLive } from "../hooks/useOrderStatusLive";
import { cancelledByLabel } from "./OrdersHUD/cancelledBy";
import RoboOutcomeDialog from "./RoboOutcomeDialog";
import UberFallbackPanel from "./UberFallbackPanel";
import { useRoboCourier } from "../hooks/useRoboCourier";
import { useAppSettings } from "../hooks/useAppSettings";
import { useChatUnread } from "../hooks/useChatUnread";
import type { PickingLineStatus } from "./OrderMissionControl/components/HolographicProductList";
import { SHOW_PRIORITY } from '../config/features';

// ============================================================================
// PAYMENT METHOD HELPER
// ============================================================================

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

interface PaymentMethodInfo {
  label: string;
  color: ChipColor;
}

const getPaymentMethodInfo = (method: string | null | undefined): PaymentMethodInfo => {
  if (!method) {
    return { label: "Paid", color: "success" };
  }

  const gatewayConfig: Record<string, PaymentMethodInfo> = {
    paygate: { label: 'Paygate', color: 'primary' },
    ozow: { label: 'Ozow', color: 'info' },
    snapscan: { label: 'SnapScan', color: 'warning' },
    zapper: { label: 'Zapper', color: 'secondary' },
    payflex: { label: 'Payflex', color: 'success' },
    payjustnow: { label: 'PayJustNow', color: 'error' },
  };

  const normalizedMethod = method.toLowerCase();
  return gatewayConfig[normalizedMethod] || { label: method, color: 'default' };
};

// ============================================================================
// CUSTOMER + DELIVERY ADDRESS HELPERS
// ============================================================================

/**
 * The id to open the customer profile with.
 *
 * `userId` is a column on `orders` so it's normally there, but the association
 * is what some responses carry — take either rather than leaving the badge
 * inert when only one of the two is present.
 */
const resolveCustomerId = (record: any): string =>
  record?.userId || record?.user?.id || '';

/** Address lines, in the order they should be read. */
const addressLines = (address: any): string[] => {
  if (!address) return [];
  return [
    [address.streetNumber, address.streetName].filter(Boolean).join(' '),
    address.buildingName,
    [address.suburb, address.city, address.province].filter(Boolean).join(', '),
    address.postalCode,
  ]
    .map((line) => (typeof line === 'string' ? line.trim() : ''))
    .filter(Boolean);
};

/**
 * Google Maps link for a delivery address.
 *
 * Coordinates win when the address has them — they're what the driver was
 * actually routed to, and they survive a misspelt street name. Otherwise fall
 * back to a text search of the address so the link still lands somewhere useful.
 */
const mapsUrl = (address: any): string | null => {
  if (!address) return null;

  const lat = Number(address.lat);
  const lng = Number(address.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  const query = addressLines(address).join(', ');
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

// ============================================================================
// ACTION MODALS (PLACEHOLDERS)
// ============================================================================

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
}

interface ReorderModalProps extends ActionModalProps {
  orderId: string;
  onSuccess?: () => void;
}

const ReorderModal: React.FC<ReorderModalProps> = ({ open, onClose, orderNumber, orderId, onSuccess }) => {
  const { mc } = useMissionTheme();
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${config.API_URL}/admin/orders/${orderId}/redispatch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ notes }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to redispatch order');
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
      alert(result.message || 'Order redispatched successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to redispatch order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.elevated,
          border: `1px solid ${mc.secondary}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: mc.textPrimary }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReplayIcon sx={{ color: mc.secondary }} />
          <Typography variant="h6">Redispatch Order #{orderNumber}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: mc.textSecondary, mb: 3 }}>
            This will reset the order to PLACED status and create a new delivery when the order is packed again.
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: `${mc.info}15`,
              border: `1px solid ${mc.info}`,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: mc.info, fontWeight: 600 }}>
              ℹ️ The system will automatically:
            </Typography>
            <List dense sx={{ mt: 1 }}>
              <ListItem>
                <ListItemIcon><InventoryIcon fontSize="small" sx={{ color: mc.info }} /></ListItemIcon>
                <ListItemText
                  primary="Validate stock availability at the store"
                  sx={{ '& .MuiListItemText-primary': { color: mc.textSecondary, fontSize: '0.875rem' } }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ReplayIcon fontSize="small" sx={{ color: mc.info }} /></ListItemIcon>
                <ListItemText
                  primary="Reset order status to PLACED"
                  sx={{ '& .MuiListItemText-primary': { color: mc.textSecondary, fontSize: '0.875rem' } }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocalShippingIcon fontSize="small" sx={{ color: mc.info }} /></ListItemIcon>
                <ListItemText
                  primary="Create new Uber delivery when packed"
                  sx={{ '& .MuiListItemText-primary': { color: mc.textSecondary, fontSize: '0.875rem' } }}
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
              Notes (Optional)
            </Typography>
            <textarea
              placeholder="Reason for redispatch..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${mc.border}`,
                backgroundColor: mc.surface,
                color: mc.textPrimary,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </Box>

          {error && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.error }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: mc.textSecondary }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: mc.secondary,
            color: 'white',
            '&:hover': {
              bgcolor: mc.secondary,
              opacity: 0.9,
            },
          }}
        >
          {loading ? 'Processing...' : 'Redispatch Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface OrderReturnedModalProps extends ActionModalProps {
  orderId: string;
  onSuccess?: () => void;
}

const OrderReturnedModal: React.FC<OrderReturnedModalProps> = ({ open, onClose, orderNumber, orderId, onSuccess }) => {
  const { mc } = useMissionTheme();
  const [stockOption, setStockOption] = useState<string>('returned');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setStockOption('returned');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const reason = stockOption === 'damaged'
        ? 'OPERATIONAL_ISSUE'
        : 'CUSTOMER_NOT_AVAILABLE';

      const fullNotes = stockOption === 'damaged'
        ? `Order returned - Items damaged/lost. ${notes}`.trim()
        : `Order returned - Stock restored to inventory. ${notes}`.trim();

      const response = await fetch(
        `${config.API_URL}/admin/orders/${orderId}/cancel-delivery`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            reason,
            notes: fullNotes,
            redispatchImmediately: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to process return');
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
      alert('Order marked as returned successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to process return');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.elevated,
          border: `1px solid ${mc.warning}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: mc.textPrimary }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentReturnIcon sx={{ color: mc.warning }} />
          <Typography variant="h6">Process Order Return - #{orderNumber}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: mc.textSecondary, mb: 3 }}>
            Mark this order as returned and specify how to handle the stock.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1.5, color: mc.textPrimary, fontWeight: 600 }}>
            Stock Handling *
          </Typography>
          <RadioGroup
            value={stockOption}
            onChange={(e) => setStockOption(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="returned"
              control={<Radio sx={{ color: mc.success }} />}
              label={
                <Box>
                  <Typography sx={{ color: mc.textPrimary, fontWeight: 500 }}>
                    Stock Returned to Inventory
                  </Typography>
                  <Typography variant="caption" sx={{ color: mc.textMuted }}>
                    Items are in good condition and can be resold
                  </Typography>
                </Box>
              }
              sx={{
                mb: 1,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${stockOption === 'returned' ? mc.success : mc.border}`,
                bgcolor: stockOption === 'returned' ? `${mc.success}10` : 'transparent',
              }}
            />
            <FormControlLabel
              value="damaged"
              control={<Radio sx={{ color: mc.error }} />}
              label={
                <Box>
                  <Typography sx={{ color: mc.textPrimary, fontWeight: 500 }}>
                    Stock Damaged/Lost
                  </Typography>
                  <Typography variant="caption" sx={{ color: mc.textMuted }}>
                    Items cannot be resold (damaged, missing, or spoiled)
                  </Typography>
                </Box>
              }
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${stockOption === 'damaged' ? mc.error : mc.border}`,
                bgcolor: stockOption === 'damaged' ? `${mc.error}10` : 'transparent',
              }}
            />
          </RadioGroup>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
              Return Notes
            </Typography>
            <textarea
              placeholder="Reason for return and condition details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${mc.border}`,
                backgroundColor: mc.surface,
                color: mc.textPrimary,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </Box>

          {stockOption === 'returned' && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.success}15`,
                border: `1px solid ${mc.success}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.success, fontWeight: 600 }}>
                ✓ Stock will be automatically restored to inventory
              </Typography>
            </Box>
          )}

          {stockOption === 'damaged' && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.error, fontWeight: 600 }}>
                ⚠️ Stock will NOT be restored. Please adjust inventory manually if needed.
              </Typography>
            </Box>
          )}

          {error && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.error }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: mc.textSecondary }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: mc.warning,
            color: 'white',
            '&:hover': {
              bgcolor: mc.warning,
              opacity: 0.9,
            },
          }}
        >
          {loading ? 'Processing...' : 'Process Return'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CancellationReason {
  value: string;
  label: string;
  icon: React.ReactElement;
  description: string;
  warningLevel: string;
  buttonColor: string;
  buttonLabel: string;
  warning?: string;
  redispatchDefault?: boolean;
  redispatchLocked?: boolean;
  showAddressInput?: boolean;
}

interface IssueModalProps extends ActionModalProps {
  status: string;
  orderId: string;
  onSuccess?: () => void;
}

/**
 * Smart Cancellation Modal
 *
 * Adapts internally based on order dispatch status:
 *
 * PRE-DISPATCH (PLACED/PICKING/PACKED):
 * - Simplified cancellation (no delivery exists yet)
 * - No redispatch options
 * - Auto-voids the Vend sale + restores stock
 *
 * POST-DISPATCH (DISPATCHED/TRACKING/ARRIVED):
 * - Full dynamic cancellation matrix
 * - Redispatch toggle per reason
 * - Address input for wrong address
 * - Conditional Vend void based on reason
 */
const IssueModal: React.FC<IssueModalProps> = ({ open, onClose, orderNumber, orderId, status, onSuccess }) => {
  const { mc } = useMissionTheme();
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [redispatch, setRedispatch] = useState<boolean>(false);
  const [newAddress, setNewAddress] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Detect if order is pre-dispatch (no Uber delivery created yet)
  const isPreDispatch = ['placed', 'picking', 'packed'].includes(status?.toLowerCase());

  // PRE-DISPATCH: Simplified cancellation reasons
  const preDispatchReasons: CancellationReason[] = [
    {
      value: 'CUSTOMER_CANCELLED',
      label: 'Customer Cancelled',
      icon: <RemoveShoppingCartIcon />,
      description: 'Customer requested cancellation before dispatch',
      warningLevel: 'error',
      buttonColor: mc.error,
      buttonLabel: 'Cancel Order',
      warning: '⚠️ This cancels the order and adds it to the Refund Queue. It does NOT void the sale in Vend — use the separate "Void Sale" button afterwards, or the POS keeps the revenue and the stock decrement.',
    },
    {
      value: 'OUT_OF_STOCK',
      label: 'Out of Stock',
      icon: <InventoryIcon />,
      description: 'Cannot fulfill order due to stock unavailability',
      warningLevel: 'warning',
      buttonColor: mc.warning,
      buttonLabel: 'Cancel Order',
      warning: '⚠️ This will VOID the sale in Vend, restore stock, and notify customer.',
    },
    {
      value: 'OPERATIONAL_ISSUE',
      label: 'Store/Operational Issue',
      icon: <ReportProblemIcon />,
      description: 'Cannot fulfill due to store closure, equipment failure, etc.',
      warningLevel: 'warning',
      buttonColor: mc.warning,
      buttonLabel: 'Cancel Order',
      warning: 'This will VOID the sale in Vend and restore stock.',
    },
    {
      value: 'OTHER',
      label: 'Other',
      icon: <ReportProblemIcon />,
      description: 'Other reason (please specify in notes)',
      warningLevel: 'info',
      buttonColor: mc.accent,
      buttonLabel: 'Cancel Order',
    },
  ];

  // POST-DISPATCH: Full cancellation matrix with redispatch options
  const postDispatchReasons: CancellationReason[] = [
    {
      value: 'WRONG_ADDRESS',
      label: 'Wrong Address / GPS Error',
      icon: <LocationOnIcon />,
      description: 'Address is incorrect or GPS coordinates are off',
      redispatchDefault: true,
      redispatchLocked: true,
      showAddressInput: true,
      warningLevel: 'info',
      buttonColor: mc.secondary,
      buttonLabel: 'Cancel & Redispatch to New Address',
    },
    {
      value: 'DRIVER_ISSUE',
      label: 'Driver Issue (Stuck / No-Show)',
      icon: <DirectionsCarIcon />,
      description: 'Driver not moving or failed to pick up',
      redispatchDefault: true,
      redispatchLocked: false,
      showAddressInput: false,
      warningLevel: 'info',
      buttonColor: mc.secondary,
      buttonLabel: 'Cancel & Find New Driver',
    },
    {
      value: 'CUSTOMER_CANCELLED',
      label: 'Customer Cancelled',
      icon: <RemoveShoppingCartIcon />,
      description: 'Customer requested cancellation',
      redispatchDefault: false,
      redispatchLocked: true, // Hidden, force false
      showAddressInput: false,
      warningLevel: 'error',
      buttonColor: mc.error,
      buttonLabel: 'Cancel Order',
      warning: '⚠️ This cancels the order and moves it to the Refund Queue. It does NOT void the sale in Vend — use the separate "Void Sale" button afterwards.',
    },
    {
      value: 'OUT_OF_STOCK',
      label: 'Store Error / Out of Stock',
      icon: <InventoryIcon />,
      description: 'Cannot fulfill order due to stock issues',
      redispatchDefault: false,
      redispatchLocked: false,
      showAddressInput: false,
      warningLevel: 'warning',
      buttonColor: mc.warning,
      buttonLabel: 'Cancel Delivery',
      warning: 'Trip will be cancelled. Please check stock levels or issue a refund manually if needed.',
    },
    {
      value: 'OTHER',
      label: 'Other',
      icon: <ReportProblemIcon />,
      description: 'Other reason (please specify in notes)',
      redispatchDefault: false,
      redispatchLocked: false,
      showAddressInput: false,
      warningLevel: 'info',
      buttonColor: mc.accent,
      buttonLabel: 'Cancel Delivery',
    },
  ];

  // Use appropriate reasons based on dispatch status
  const cancellationReasons = isPreDispatch ? preDispatchReasons : postDispatchReasons;
  const selectedReason = cancellationReasons.find((r) => r.value === reason);

  // Update redispatch checkbox when reason changes (post-dispatch only)
  React.useEffect(() => {
    if (selectedReason && 'redispatchDefault' in selectedReason) {
      setRedispatch(selectedReason.redispatchDefault || false);
    } else {
      // Pre-dispatch: no redispatch option
      setRedispatch(false);
    }
  }, [reason]);

  const handleClose = () => {
    setReason('');
    setNotes('');
    setRedispatch(false);
    setNewAddress('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError('Please select a cancellation reason');
      return;
    }

    // Only validate address for post-dispatch redispatch scenarios
    if (!isPreDispatch && selectedReason?.showAddressInput && redispatch && !newAddress) {
      setError('Please enter a new delivery address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const payload: any = {
        reason,
        notes,
        redispatchImmediately: redispatch,
      };

      // Add new address if provided
      if (newAddress && redispatch) {
        // TODO: Parse address string into structured format
        // For now, send as notes
        payload.notes = `${notes}\nNew Address: ${newAddress}`.trim();
      }

      const response = await fetch(
        `${config.API_URL}/admin/orders/${orderId}/cancel-delivery`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to cancel delivery');
      }

      const result = await response.json();

      // Success notification
      const successMessage = result.redispatched
        ? `Delivery cancelled and redispatched successfully`
        : result.vendVoidedAt
        ? `Delivery cancelled and sale voided in Vend`
        : `Delivery cancelled successfully`;

      // Trigger success callback (refresh order data)
      if (onSuccess) {
        onSuccess();
      }

      handleClose();

      // Show success message (you may want to use a toast/snackbar here)
      alert(successMessage);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel delivery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.elevated,
          border: `1px solid ${selectedReason?.buttonColor || mc.error}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: mc.textPrimary }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RemoveShoppingCartIcon sx={{ color: selectedReason?.buttonColor || mc.error }} />
          <Typography variant="h6">
            {isPreDispatch ? 'Cancel Order' : 'Cancel Delivery'} - Order #{orderNumber}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: mc.textMuted, mb: 3 }}>
            {isPreDispatch
              ? 'Select a cancellation reason. This does NOT void the sale in Vend — void it separately afterwards if the sale should not stand.'
              : 'Select a cancellation reason. The system will automatically handle Uber cancellation, Vend sync, and stock restoration.'}
          </Typography>

          {/* Cancellation Reason Selection */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: mc.textPrimary, fontWeight: 600 }}>
            Cancellation Reason *
          </Typography>
          <List sx={{ mb: 2 }}>
            {cancellationReasons.map((r) => (
              <ListItemButton
                key={r.value}
                selected={reason === r.value}
                onClick={() => setReason(r.value)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: `1px solid ${reason === r.value ? r.buttonColor : mc.border}`,
                  bgcolor: reason === r.value ? `${r.buttonColor}15` : 'transparent',
                  '&:hover': {
                    bgcolor: `${r.buttonColor}10`,
                    borderColor: r.buttonColor,
                  },
                }}
              >
                <ListItemIcon sx={{ color: reason === r.value ? r.buttonColor : mc.textMuted }}>
                  {r.icon}
                </ListItemIcon>
                <ListItemText
                  primary={r.label}
                  secondary={r.description}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: reason === r.value ? r.buttonColor : mc.textPrimary,
                      fontWeight: reason === r.value ? 600 : 400,
                    },
                    '& .MuiListItemText-secondary': { color: mc.textMuted, fontSize: '0.75rem' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>

          {/* Redispatch Checkbox (POST-DISPATCH ONLY) */}
          {!isPreDispatch && selectedReason && 'redispatchLocked' in selectedReason && selectedReason.value !== 'CUSTOMER_CANCELLED' && (
            <Box sx={{ mb: 2, p: 2, bgcolor: mc.surface, borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={redispatch}
                    onChange={(e) => !selectedReason.redispatchLocked && setRedispatch(e.target.checked)}
                    disabled={selectedReason.redispatchLocked}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: mc.secondary,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: mc.secondary,
                      },
                    }}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1} component="span">
                    <Typography component="span" sx={{ color: mc.textPrimary }}>
                      Re-dispatch immediately
                    </Typography>
                    {selectedReason.redispatchLocked && (
                      <Chip
                        label="AUTO"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: `${mc.secondary}20`,
                          color: mc.secondary,
                        }}
                      />
                    )}
                  </Stack>
                }
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: mc.textMuted }}>
                {selectedReason.redispatchLocked
                  ? 'Automatically enabled for this reason'
                  : 'Create a new delivery after cancellation'}
              </Typography>
            </Box>
          )}

          {/* New Address Input (Conditional) */}
          {selectedReason?.showAddressInput && redispatch && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
                New Delivery Address *
              </Typography>
              <input
                type="text"
                placeholder="Enter new address..."
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${mc.border}`,
                  backgroundColor: mc.surface,
                  color: mc.textPrimary,
                  fontSize: '14px',
                  fontFamily: 'inherit',
                }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: mc.textMuted }}>
                The order will be redispatched to this new address
              </Typography>
            </Box>
          )}

          {/* Notes Input */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
              Additional Notes
            </Typography>
            <textarea
              placeholder="Optional notes about this cancellation..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${mc.border}`,
                backgroundColor: mc.surface,
                color: mc.textPrimary,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </Box>

          {/* Warning Messages */}
          {selectedReason?.warning && (
            <Box
              sx={{
                p: 2,
                bgcolor: selectedReason.warningLevel === 'error'
                  ? `${mc.error}15`
                  : `${mc.warning}15`,
                border: `1px solid ${selectedReason.warningLevel === 'error' ? mc.error : mc.warning}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: selectedReason.warningLevel === 'error' ? mc.error : mc.warning,
                  fontWeight: 600,
                }}
              >
                {selectedReason.warning}
              </Typography>
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.error }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: mc.textSecondary }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !reason}
          variant="contained"
          sx={{
            bgcolor: selectedReason?.buttonColor || mc.accent,
            color: 'white',
            '&:hover': {
              bgcolor: selectedReason?.buttonColor || mc.accent,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              bgcolor: mc.surface,
              color: mc.textMuted,
            },
          }}
        >
          {loading ? 'Processing...' : selectedReason?.buttonLabel || 'Cancel Delivery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// PROOF OF DELIVERY MODAL
// ============================================================================

interface PODModalProps extends ActionModalProps {
  orderId: string;
}

const ProofOfDeliveryModal: React.FC<PODModalProps> = ({ open, onClose, orderNumber, orderId }) => {
  const { mc } = useMissionTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [podData, setPodData] = useState<any>(null);

  React.useEffect(() => {
    if (open && orderId) {
      fetchPOD();
    }
  }, [open, orderId]);

  const fetchPOD = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${config.API_URL}/admin/orders/${orderId}/pod`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch proof of delivery');
      }

      const data = await response.json();
      setPodData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch proof of delivery');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPodData(null);
    setError(null);
    setLoading(true);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.elevated,
          border: `1px solid ${mc.info}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: mc.textPrimary }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrintIcon sx={{ color: mc.info }} />
          <Typography variant="h6">Proof of Delivery - Order #{orderNumber}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {loading && (
            <Typography sx={{ color: mc.textSecondary, textAlign: 'center', py: 4 }}>
              Loading proof of delivery...
            </Typography>
          )}

          {error && (
            <Box
              sx={{
                p: 3,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" sx={{ color: mc.error }}>
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && podData && (
            <>
              {podData.message && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: `${mc.info}15`,
                    border: `1px solid ${mc.info}`,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: mc.info }}>
                    {podData.message}
                  </Typography>
                </Box>
              )}

              {podData.verification && (
                <Stack spacing={3}>
                  {/* Signature */}
                  {podData.verification.signature?.collected && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: mc.textPrimary, mb: 1, fontWeight: 600 }}>
                        Customer Signature
                      </Typography>
                      {podData.verification.signature.imageUrl ? (
                        <Box
                          component="img"
                          src={podData.verification.signature.imageUrl}
                          alt="Customer signature"
                          sx={{
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'contain',
                            border: `1px solid ${mc.border}`,
                            borderRadius: 2,
                            bgcolor: 'white',
                            p: 2,
                          }}
                        />
                      ) : null}
                      {podData.verification.signature.signerName && (
                        <Typography variant="body2" sx={{ color: mc.textSecondary, mt: 1 }}>
                          Signed by: {podData.verification.signature.signerName}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Photo */}
                  {podData.verification.picture?.collected && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: mc.textPrimary, mb: 1, fontWeight: 600 }}>
                        Delivery Photo
                      </Typography>
                      {podData.verification.picture.imageUrl ? (
                        <Box
                          component="img"
                          src={podData.verification.picture.imageUrl}
                          alt="Delivery photo"
                          sx={{
                            width: '100%',
                            maxHeight: 400,
                            objectFit: 'contain',
                            border: `1px solid ${mc.border}`,
                            borderRadius: 2,
                          }}
                        />
                      ) : null}
                    </Box>
                  )}

                  {/* PIN Verification */}
                  {podData.verification.pincode && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: `${mc.success}15`,
                        border: `1px solid ${mc.success}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: mc.success, fontWeight: 600 }}>
                        ✓ Customer verified delivery with PIN code
                      </Typography>
                    </Box>
                  )}

                  {/* Completion Time */}
                  {podData.completedAt && (
                    <Box>
                      <Typography variant="caption" sx={{ color: mc.textMuted }}>
                        Delivered at: {new Date(podData.completedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} sx={{ color: mc.textSecondary }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// REQUEST REFUND MODAL
// ============================================================================

interface RefundModalProps extends ActionModalProps {
  orderId: string;
  orderTotal: number;
  onSuccess?: () => void;
}

const RequestRefundModal: React.FC<RefundModalProps> = ({ open, onClose, orderNumber, orderId, orderTotal: rawTotal, onSuccess }) => {
  const { mc } = useMissionTheme();
  const orderTotal = typeof rawTotal === 'number' ? rawTotal : parseFloat(rawTotal) || 0;
  const [reason, setReason] = useState<string>('');
  const [amount, setAmount] = useState<string>(orderTotal.toFixed(2));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setAmount(orderTotal.toFixed(2));
    }
  }, [open, orderTotal]);

  const handleClose = () => {
    setReason('');
    setAmount(orderTotal.toFixed(2));
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for the refund');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid refund amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${config.API_URL}/admin/orders/${orderId}/request-refund`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            reason,
            amount: amountNum,
            resendItems: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to request refund');
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
      alert(result.message || 'Refund request submitted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to request refund');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mc.elevated,
          border: `1px solid ${mc.gold}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: mc.textPrimary }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachMoneyIcon sx={{ color: mc.gold }} />
          <Typography variant="h6">Request Refund - Order #{orderNumber}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: mc.textSecondary, mb: 3 }}>
            Submit a refund request to the finance team. They will process the payment reversal.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
              Refund Amount (ZAR) *
            </Typography>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${mc.border}`,
                backgroundColor: mc.surface,
                color: mc.textPrimary,
                fontSize: '16px',
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: mc.textMuted }}>
              Original order total: R{orderTotal.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: mc.textPrimary }}>
              Refund Reason *
            </Typography>
            <textarea
              placeholder="Explain why this refund is needed..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${mc.border}`,
                backgroundColor: mc.surface,
                color: mc.textPrimary,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: `${mc.info}15`,
              border: `1px solid ${mc.info}`,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: mc.info }}>
              📧 An email will be sent to the finance team for manual processing.
            </Typography>
          </Box>

          {error && (
            <Box
              sx={{
                p: 2,
                bgcolor: `${mc.error}15`,
                border: `1px solid ${mc.error}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: mc.error }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: mc.textSecondary }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: mc.gold,
            color: 'white',
            '&:hover': {
              bgcolor: mc.gold,
              opacity: 0.9,
            },
          }}
        >
          {loading ? 'Submitting...' : 'Submit Refund Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// DELIVERY NOTE BUTTON (with popup)
// ============================================================================

interface DeliveryNoteButtonProps {
  note: string;
}

const DeliveryNoteButton: React.FC<DeliveryNoteButtonProps> = ({ note }) => {
  const { mc } = useMissionTheme();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<NoteIcon />}
        onClick={() => setOpen(true)}
        sx={{
          borderColor: mc.warning,
          color: mc.warning,
          textTransform: 'none',
          '&:hover': {
            borderColor: mc.warning,
            bgcolor: `${mc.warning}15`,
          },
        }}
      >
        Delivery Note
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: mc.elevated,
            border: `1px solid ${mc.warning}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: mc.textPrimary }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <NoteIcon sx={{ color: mc.warning }} />
            <Typography variant="h6">Customer Delivery Note</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 2,
              p: 3,
              bgcolor: mc.surface,
              borderRadius: 2,
              border: `1px solid ${mc.border}`,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: mc.textPrimary,
                fontStyle: 'italic',
                lineHeight: 1.8,
              }}
            >
              "{note}"
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: mc.textMuted }}>
            This note was provided by the customer during checkout and is sent to the delivery driver.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Tooltip title="Copy to clipboard">
            <IconButton
              onClick={() => navigator.clipboard.writeText(note)}
              sx={{ color: mc.textSecondary }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Button onClick={() => setOpen(false)} sx={{ color: mc.textSecondary }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================================
// RECORD SYNC HELPER
// ============================================================================

/** Syncs the react-admin record context to parent state via useEffect (avoids render-phase setState). */
function RecordSync({ onRecord }: { onRecord: (record: any) => void }) {
  const record = useRecordContext();
  useEffect(() => {
    if (record?.id) onRecord(record);
  }, [record, onRecord]);
  return null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ShowOrderItem() {
  const { mc } = useMissionTheme();
  const RecordId = useGetRecordId();

  // Animation uses theme-aware accent glow color
  const glowPulse = useMemo(
    () => keyframes`
      0%, 100% { box-shadow: 0 0 20px ${mc.accentGlow}; }
      50% { box-shadow: 0 0 40px ${mc.accentGlow}; }
    `,
    [mc.accentGlow],
  );

  const { role } = usePermissions();
  const [statusSaving, setStatusSaving] = useState(false);
  const [update] = useUpdate();

  // ---- Void the Vend sale -------------------------------------------------
  // Cancelling or returning an order no longer voids its Vend sale on its own: a void
  // is irreversible in Vend, so it needs a person to confirm it. Everything below
  // drives that one explicit action.
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voidedAt, setVoidedAt] = useState<string | null>(null);

  const handleVoidSale = async (orderId: string) => {
    setVoidLoading(true);
    setVoidError(null);
    try {
      const token = getStoredToken();
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${config.API_URL}/admin/orders/${orderId}/void-sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: 'Voided from admin dashboard' }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Failed to void the sale');

      // Reflect it immediately — the list refetch can lag, and leaving the button
      // live invites a second click on an irreversible action.
      setVoidedAt(result.vendVoidedAt || new Date().toISOString());
      setVoidConfirmOpen(false);
    } catch (err: any) {
      setVoidError(err.message || 'Failed to void the sale');
    } finally {
      setVoidLoading(false);
    }
  };

  const saveStatus = async (data: Record<string, unknown>) => {
    if (!currentRecord?.id) return;
    setStatusSaving(true);
    try {
      await update('orders', { id: currentRecord.id, data, previousData: currentRecord }, { returnPromise: true });
    } finally {
      setStatusSaving(false);
    }
  };

  // Test environments (Loop robo courier on): packing first asks which ending the robo
  // courier should play. Otherwise packing goes straight through, as before.
  const roboCourierOn = useRoboCourier();
  const [roboPack, setRoboPack] = useState<Record<string, unknown> | null>(null);
  const pack = (data: Record<string, unknown>) => (roboCourierOn ? setRoboPack(data) : saveStatus(data));

  const handleStatusAdvance = async (nextStatus: string) => {
    if (nextStatus === 'packed') return pack({ status: 'packed' });
    return saveStatus({ status: nextStatus });
  };

  const handlePackConfirmed = async (size: string) => pack({ status: 'packed', parcelSize: size });

  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [podModalOpen, setPodModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  // App settings (feature gate)
  const { settings: appSettings } = useAppSettings();
  const companionEnabled = appSettings?.enableCompanionApp ?? false;

  // Live status: when this order's status changes anywhere (courier, app, another admin,
  // the Loop robo courier), reload it — otherwise the page showed the old status until a
  // manual refresh.
  const refresh = useRefresh();
  useOrderStatusLive(currentRecord?.storeId, currentRecord?.id, () => refresh());

  // Real-time picking status from Socket.IO (only when companion app enabled)
  const storeId = companionEnabled ? (currentRecord?.storeId || null) : null;
  const { getOrderPickingState } = usePickingStatus(storeId);
  const pickingState = companionEnabled && currentRecord?.id ? getOrderPickingState(currentRecord.id) : undefined;

  // Clear chat unread count when viewing an order
  const { clearOrder } = useChatUnread();
  useEffect(() => {
    if (currentRecord?.id) clearOrder(currentRecord.id);
  }, [currentRecord?.id, clearOrder]);

  // Customer stats fetched from API (dynamic, not denormalized)
  const [customerStats, setCustomerStats] = useState<{
    totalOrders: number;
    totalOrderValue: number;
    loading: boolean;
  }>({ totalOrders: 0, totalOrderValue: 0, loading: true });

  // Fetch customer stats when record changes
  useEffect(() => {
    const customerId = resolveCustomerId(currentRecord);

    const fetchCustomerStats = async () => {
      if (!customerId) {
        setCustomerStats({ totalOrders: 0, totalOrderValue: 0, loading: false });
        return;
      }

      try {
        const token = getStoredToken();
        if (!token) {
          setCustomerStats({ totalOrders: 0, totalOrderValue: 0, loading: false });
          return;
        }

        const response = await fetch(
          `${config.API_URL}/admin/users/customers/${customerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setCustomerStats({
            totalOrders: data.customer?.totalOrders || 0,
            totalOrderValue: data.customer?.totalOrderValue || 0,
            loading: false,
          });
        } else {
          setCustomerStats({ totalOrders: 0, totalOrderValue: 0, loading: false });
        }
      } catch (error) {
        setCustomerStats({ totalOrders: 0, totalOrderValue: 0, loading: false });
      }
    };

    fetchCustomerStats();
  }, [currentRecord?.userId, currentRecord?.user?.id]);

  // Calculate customer VIP tier based on order count
  const getCustomerTier = (totalOrders: number): { tier: string; color: string; icon: string } => {
    if (totalOrders >= 50) return { tier: 'VIP', color: mc.gold, icon: '👑' };
    if (totalOrders >= 20) return { tier: 'ELITE', color: mc.accent, icon: '💎' };
    if (totalOrders >= 10) return { tier: 'LOYAL', color: mc.secondary, icon: '⭐' };
    if (totalOrders >= 3) return { tier: 'REGULAR', color: mc.success, icon: '✓' };
    return { tier: 'NEW', color: mc.info, icon: '🆕' };
  };

  const numberFormat = (record: any, toFixed: number) => {
    return Number(record).toFixed(toFixed);
  };

  return (
    <Edit
      sx={{
        bgcolor: mc.background,
        minHeight: '100vh',
        '& .RaEdit-main': {
          bgcolor: mc.background,
        },
        '& .RaEdit-card': {
          bgcolor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <>
        {/* Record sync - sets currentRecord for customer stats fetch */}
        <RecordSync onRecord={setCurrentRecord} />

        {/* ================================================================ */}
        {/* MISSION HEADER */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box
              sx={{
                px: 3,
                pt: 3,
                pb: 2,
                borderBottom: `1px solid ${mc.border}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                {/* Order Info */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: mc.textPrimary,
                        fontFamily: 'monospace',
                        letterSpacing: 1,
                      }}
                    >
                      ORDER #{record.number}
                    </Typography>
                    <Tooltip title="Copy order ID">
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(record.number)}
                        sx={{ color: mc.textMuted }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" sx={{ color: mc.textMuted, mt: 0.5 }}>
                    {new Date(record.createdAt).toLocaleString("en-ZA", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>

                {/* Quick Actions */}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {record.user?.phoneNumber && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhoneIcon />}
                      href={`tel:${record.user.phoneNumber}`}
                      sx={{
                        borderColor: mc.success,
                        color: mc.success,
                        '&:hover': {
                          borderColor: mc.success,
                          bgcolor: `${mc.success}20`,
                        },
                      }}
                    >
                      Call
                    </Button>
                  )}
                  <DownloadInvoice RecordId={RecordId} orderNumber={record.number} variant="button" />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListAltIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      runDocumentAction(printDocument("slip", String(RecordId)));
                    }}
                    sx={{ borderColor: "text.secondary", color: "text.primary" }}
                  >
                    Print Slip
                  </Button>
                  <Tooltip title="Download the slip as a PDF">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        runDocumentAction(
                          downloadDocument("slip", String(RecordId), `${record.number} Order Slip.pdf`),
                        );
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {record.status === 'delivered' && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => {
                          setCurrentRecord(record);
                          setPodModalOpen(true);
                        }}
                        sx={{
                          borderColor: mc.info,
                          color: mc.info,
                          '&:hover': {
                            borderColor: mc.info,
                            bgcolor: `${mc.info}20`,
                          },
                        }}
                      >
                        POD
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={() => {
                          setCurrentRecord(record);
                          setReorderModalOpen(true);
                        }}
                        sx={{
                          borderColor: mc.secondary,
                          color: mc.secondary,
                          '&:hover': {
                            borderColor: mc.secondary,
                            bgcolor: `${mc.secondary}20`,
                          },
                        }}
                      >
                        Reorder
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentReturnIcon />}
                        onClick={() => {
                          setCurrentRecord(record);
                          setReturnModalOpen(true);
                        }}
                        sx={{
                          borderColor: mc.warning,
                          color: mc.warning,
                          '&:hover': {
                            borderColor: mc.warning,
                            bgcolor: `${mc.warning}20`,
                          },
                        }}
                      >
                        Return
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachMoneyIcon />}
                        onClick={() => {
                          setCurrentRecord(record);
                          setRefundModalOpen(true);
                        }}
                        sx={{
                          borderColor: mc.gold,
                          color: mc.gold,
                          '&:hover': {
                            borderColor: mc.gold,
                            bgcolor: `${mc.gold}20`,
                          },
                        }}
                      >
                        Refund
                      </Button>
                    </>
                  )}

                  {!['delivered', 'cancelled', 'returned'].includes(record.status) && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RemoveShoppingCartIcon />}
                      onClick={() => {
                        setCurrentRecord(record);
                        setIssueModalOpen(true);
                      }}
                      sx={{
                        borderColor: mc.error,
                        color: mc.error,
                        '&:hover': {
                          borderColor: mc.error,
                          bgcolor: `${mc.error}20`,
                        },
                      }}
                    >
                      {['placed', 'picking', 'packed'].includes(record.status) ? 'Cancel Order' : 'Cancel Delivery'}
                    </Button>
                  )}

                  {/* Void the Vend sale — only once the order is cancelled or returned,
                      only while a sale is actually live, and never without confirming.
                      A void cannot be undone in Vend. */}
                  {['cancelled', 'returned'].includes(record.status)
                    && record.vendSaleId
                    && !(record.vendVoidedAt || voidedAt) && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<RemoveShoppingCartIcon />}
                      onClick={() => {
                        setCurrentRecord(record);
                        setVoidError(null);
                        setVoidConfirmOpen(true);
                      }}
                      sx={{
                        bgcolor: mc.error,
                        color: 'white',
                        '&:hover': { bgcolor: mc.error, filter: 'brightness(1.1)' },
                      }}
                    >
                      Void Sale?
                    </Button>
                  )}

                  {/* Loop's order.returned, confirmed with Loop: the goods physically came
                      back to the store — the case where voiding (and so restocking) is right. */}
                  {/* Who ended a cancelled order: the customer, the driver (delivery failed) or us. */}
                  {cancelledByLabel(record) && (
                    <Chip
                      size="small"
                      label={`Cancelled ${cancelledByLabel(record)}`}
                      sx={{ bgcolor: `${mc.textSecondary}20`, color: mc.textSecondary, fontWeight: 600 }}
                    />
                  )}

                  {record.returnedToStoreAt && (
                    <Chip
                      size="small"
                      label={`Goods returned to store ${new Date(record.returnedToStoreAt).toLocaleString()}`}
                      sx={{ bgcolor: `${mc.textSecondary}20`, color: mc.textSecondary, fontWeight: 600 }}
                    />
                  )}

                  {(record.vendVoidedAt || voidedAt) && (
                    <Chip
                      size="small"
                      label={`Sale voided ${new Date(record.vendVoidedAt || voidedAt).toLocaleString()}`}
                      sx={{ bgcolor: `${mc.textSecondary}20`, color: mc.textSecondary, fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Stack>

              {/* Summary Chips */}
              <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap">
                <Chip
                  label={`R${Number(record.total || 0).toFixed(2)}`}
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    bgcolor: mc.accent,
                    color: 'white',
                    animation: `${glowPulse} 3s ease-in-out infinite`,
                  }}
                />
                {/* Paid/Unpaid is the signal; the gateway gets its own chip beside
                    it, so the strip reads: R200 · Paid · Ozow · N items · Store */}
                {record.isPaid ? (
                  <>
                    <Chip
                      icon={<CreditCardIcon sx={{ color: 'inherit !important' }} />}
                      label="Paid"
                      sx={{
                        bgcolor: `${mc.success}20`,
                        color: mc.success,
                        border: `1px solid ${mc.success}`,
                      }}
                    />
                    {record.paymentMethod && (
                      <Chip
                        label={getPaymentMethodInfo(record.paymentMethod).label}
                        sx={{
                          bgcolor: mc.surface,
                          color: mc.textSecondary,
                          border: `1px solid ${mc.border}`,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Chip
                    label="Unpaid"
                    sx={{
                      bgcolor: `${mc.warning}20`,
                      color: mc.warning,
                      border: `1px solid ${mc.warning}`,
                    }}
                  />
                )}
                <Chip
                  label={`${record.lines?.length || 0} items`}
                  sx={{
                    bgcolor: mc.surface,
                    color: mc.textSecondary,
                    border: `1px solid ${mc.border}`,
                  }}
                />
                <Chip
                  icon={<StoreIcon sx={{ color: 'inherit !important' }} />}
                  label={record.store?.name || record.storeId || "Unknown"}
                  sx={{
                    bgcolor: mc.surface,
                    color: mc.textSecondary,
                    border: `1px solid ${mc.border}`,
                  }}
                />
              </Stack>
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* POWER LINE (Status Timeline) */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, py: 2 }}>
              <PowerLine
                status={record.status}
                createdAt={record.createdAt}
                packedAt={record.packedAt}
                dispatchedAt={record.dispatchedAt}
                deliveredAt={record.deliveredAt}
                cancellationReason={record.cancellationReason}
                returnReason={record.returnReason}
              />
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* CUSTOMER INTEL RADAR */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, pb: 3 }}>
              {SHOW_PRIORITY && (
                <CustomerIntelRadar
                  orderTotal={record.total || 0}
                  driverTip={record.totalDriverTip || 0}
                  coupons={record.coupons}
                  promotions={record.promotions}
                  user={record.user}
                  customerTotalOrders={customerStats.loading ? undefined : customerStats.totalOrders}
                />
              )}
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* INFO MODULES (Two Column Grid) */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, pb: 3 }}>
              <Grid container spacing={3} alignItems="stretch">
                {/* ROW 1: Customer + Delivery Coordinates */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <GlassCard
                    title="Customer"
                    icon={<PersonIcon />}
                    iconColor={mc.secondary}
                    sx={{ height: '100%' }}
                  >
                    {/* Clickable Customer Badge */}
                    <Box
                      onClick={() => {
                        if (resolveCustomerId(record)) {
                          setCurrentRecord(record);
                          setCustomerModalOpen(true);
                        }
                      }}
                      sx={{
                        cursor: resolveCustomerId(record) ? 'pointer' : 'default',
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: mc.surface,
                        border: `1px solid ${mc.border}`,
                        transition: 'all 0.2s ease',
                        '&:hover': resolveCustomerId(record) ? {
                          bgcolor: `${mc.secondary}10`,
                          borderColor: mc.secondary,
                        } : {},
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: mc.elevated,
                            border: `2px solid ${getCustomerTier(customerStats.totalOrders).color}`,
                            color: getCustomerTier(customerStats.totalOrders).color,
                            fontSize: '1.2rem',
                          }}
                        >
                          {record.user?.firstName?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="h6"
                              sx={{ color: mc.textPrimary, fontWeight: 600 }}
                            >
                              {record.user?.firstName} {record.user?.lastName}
                            </Typography>
                            {SHOW_PRIORITY && (
                              <Chip
                                label={`${getCustomerTier(customerStats.totalOrders).icon} ${getCustomerTier(customerStats.totalOrders).tier}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  bgcolor: `${getCustomerTier(customerStats.totalOrders).color}20`,
                                  color: getCustomerTier(customerStats.totalOrders).color,
                                  border: `1px solid ${getCustomerTier(customerStats.totalOrders).color}`,
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" sx={{ color: mc.textMuted }}>
                            {customerStats.loading ? '...' : customerStats.totalOrders} orders
                            {resolveCustomerId(record) ? ' • Click to view profile' : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    {record.user?.email && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: mc.textSecondary, wordBreak: 'break-all' }}
                        >
                          {record.user.email}
                        </Typography>
                        <Tooltip title="Copy email address">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(record.user.email)}
                            sx={{ color: mc.textMuted }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {record.user?.phoneNumber && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PhoneIcon fontSize="small" sx={{ color: mc.success }} />
                        <Link
                          href={`tel:${record.user.phoneNumber}`}
                          sx={{ color: mc.success, textDecoration: 'none' }}
                        >
                          {record.user.phoneNumber}
                        </Link>
                      </Stack>
                    )}
                  </GlassCard>
                </Grid>
                {/* Where this order is going. Origin Base used to sit here; the
                    fulfilling store now lives in the customer profile modal, so
                    the destination gets the slot next to the customer instead. */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <GlassCard
                    title="Delivery Coordinates"
                    icon={<LocationOnIcon />}
                    iconColor={mc.error}
                    sx={{ height: '100%' }}
                  >
                    {(() => {
                      const address = record.shippingAddress;
                      const lines = addressLines(address);
                      const url = mapsUrl(address);

                      if (!lines.length) {
                        return (
                          <Typography variant="body2" sx={{ color: mc.textMuted }}>
                            {record.shippingAddressId
                              ? 'Address not returned with this order'
                              : 'Collection order — no delivery address'}
                          </Typography>
                        );
                      }

                      return (
                        <Stack spacing={0.5}>
                          {address?.friendlyName && (
                            <Typography
                              variant="caption"
                              sx={{ color: mc.textMuted, letterSpacing: '0.08em' }}
                            >
                              {String(address.friendlyName).toUpperCase()}
                            </Typography>
                          )}
                          {lines.map((line, idx) => (
                            <Typography
                              key={line}
                              variant="body2"
                              sx={{ color: idx === 0 ? mc.textPrimary : mc.textSecondary }}
                            >
                              {line}
                            </Typography>
                          ))}
                          {url && (
                            <Link
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 1,
                                color: mc.secondary,
                                textDecoration: 'none',
                                '&:hover': { color: mc.success },
                              }}
                            >
                              Open in Google Maps
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </Link>
                          )}
                        </Stack>
                      );
                    })()}
                  </GlassCard>
                </Grid>

                {/* ROW 2: Delivery Intel */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <GlassCard
                    title="Delivery Intel"
                    icon={<LocalShippingIcon />}
                    iconColor={mc.success}
                    sx={{ height: '100%' }}
                  >
                    <Stack spacing={1.5}>
                      {record.trackingUrl ? (
                        <Link
                          href={record.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: mc.secondary,
                            textDecoration: 'none',
                            '&:hover': { color: mc.success },
                          }}
                        >
                          Live Tracking
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ color: mc.textMuted }}>
                          No tracking available yet
                        </Typography>
                      )}
                      <GlassInfoRow label="Parcel Size" value={record.parcelSize || "medium"} />
                      {record.deliveryPin && (
                        <GlassInfoRow label="Delivery PIN" value={record.deliveryPin} highlight />
                      )}
                      {/* Loop collection OTP: store staff give it to the driver at pickup.
                          Staff-only, and never the same as the return code. */}
                      {record.collectionPin && (
                        <GlassInfoRow label="Collection code (give to driver at pickup)" value={record.collectionPin} />
                      )}
                      {/* Loop return OTP: store staff give it to the driver when an
                          undeliverable parcel is brought back. Staff-only. */}
                      {record.returnPin && (
                        <GlassInfoRow label="Return code (give to driver on return)" value={record.returnPin} />
                      )}
                      {record.uberDirectId && (
                        <Typography variant="caption" sx={{ color: mc.textMuted, fontFamily: 'monospace' }}>
                          Uber ID: {record.uberDirectId}
                        </Typography>
                      )}
                      {/* Delivery Note Button */}
                      {record.shippingNote && (
                        <DeliveryNoteButton note={record.shippingNote} />
                      )}
                    </Stack>
                  </GlassCard>
                </Grid>
              </Grid>

              {/* DRIVER CARD (Full Width) */}
              {record.driverName && (
                <Box sx={{ mt: 3 }}>
                  <GlassCard
                    title="Courier Operative"
                    icon={<DirectionsCarFilledIcon />}
                    iconColor={mc.success}
                    variant="highlight"
                  >
                    <Stack direction="row" spacing={3} alignItems="center">
                      <Avatar
                        src={record.driverPhotoUrl || undefined}
                        alt={record.driverName}
                        sx={{
                          width: 72,
                          height: 72,
                          border: `3px solid ${mc.success}`,
                          boxShadow: `0 0 20px ${mc.successGlow}`,
                        }}
                      >
                        {record.driverName?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="h6"
                            sx={{ color: mc.textPrimary, fontWeight: 600 }}
                          >
                            {record.driverName}
                          </Typography>
                          {record.driverRating && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                              label={record.driverRating}
                              size="small"
                              sx={{
                                height: 22,
                                bgcolor: `${mc.gold}20`,
                                color: mc.gold,
                                border: `1px solid ${mc.gold}`,
                              }}
                            />
                          )}
                        </Stack>
                        {(record.driverVehicleMake || record.driverVehicleModel) && (
                          <Typography variant="body2" sx={{ color: mc.textSecondary, mt: 0.5 }}>
                            {[record.driverVehicleColor, record.driverVehicleMake, record.driverVehicleModel]
                              .filter(Boolean)
                              .join(" ")}
                          </Typography>
                        )}
                        {record.driverVehiclePlate && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: mc.secondary,
                              mt: 0.5,
                            }}
                          >
                            {record.driverVehiclePlate}
                          </Typography>
                        )}
                      </Box>
                      {record.driverPhone && (
                        <Button
                          variant="outlined"
                          startIcon={<PhoneIcon />}
                          href={`tel:${record.driverPhone}`}
                          sx={{
                            borderColor: mc.success,
                            color: mc.success,
                          }}
                        >
                          Call Driver
                        </Button>
                      )}
                    </Stack>
                  </GlassCard>
                </Box>
              )}
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* ACTIVE PICKER CARD (companion app only) */}
        {/* ================================================================ */}
        {companionEnabled && pickingState?.picker && currentRecord?.status === 'picking' && (
          <Box sx={{ px: 3, pb: 2 }}>
            <GlassCard
              title="Active Picker"
              icon={<PersonIcon />}
              iconColor={mc.accent}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: mc.accent,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  }}
                >
                  {pickingState.picker.name?.[0]?.toUpperCase() || 'P'}
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: mc.textPrimary, fontWeight: 600 }}
                  >
                    {pickingState.picker.name}
                  </Typography>
                  {pickingState.startedAt && (
                    <Typography
                      variant="caption"
                      sx={{ color: mc.textSecondary }}
                    >
                      Started {new Date(pickingState.startedAt).toLocaleTimeString()}
                    </Typography>
                  )}
                </Box>
                {pickingState.progress && (
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: mc.accent,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {pickingState.progress.found + pickingState.progress.notFound}/{pickingState.progress.total}
                    </Typography>
                    <Typography variant="caption" sx={{ color: mc.textMuted }}>
                      items processed
                    </Typography>
                  </Box>
                )}
              </Stack>
              {pickingState.hasNotFoundItems && (
                <Chip
                  label="HAS MISSING ITEMS"
                  size="small"
                  sx={{
                    mt: 1.5,
                    bgcolor: 'rgba(239, 68, 68, 0.15)',
                    color: '#EF4444',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    letterSpacing: '0.05em',
                  }}
                />
              )}
            </GlassCard>
          </Box>
        )}

        {/* ================================================================ */}
        {/* CHAT PANEL (live chat during picking — companion app only) */}
        {/* ================================================================ */}
        {companionEnabled && currentRecord?.status === 'picking' && (
          <Box sx={{ px: 3, pb: 2 }}>
            <ChatPanel orderId={currentRecord.id} />
          </Box>
        )}

        {/* Loop gave up or found no driver: offer Uber Direct (head office). */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3 }}>
              <UberFallbackPanel orderId={record.id} fallback={record.uberFallback} loopRetry={record.loopRetry} onDone={() => refresh()} />
            </Box>
          )}
        />

        <RoboOutcomeDialog
          open={roboPack !== null}
          onCancel={() => setRoboPack(null)}
          onChoose={(roboOutcome) => {
            const data = roboPack;
            setRoboPack(null);
            if (data) saveStatus({ ...data, roboOutcome });
          }}
        />

        {/* ================================================================ */}
        {/* HOLOGRAPHIC PRODUCT LIST */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, pb: 3 }}>
              <HolographicProductList
                lines={record.lines || []}
                status={record.status}
                role={role}
                onPackConfirmed={handlePackConfirmed}
                pickingStatuses={companionEnabled ? pickingState?.lineStatuses : undefined}
              />
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* PROOF PHOTOS (picking photos + proof-of-pack — companion app only) */}
        {/* ================================================================ */}
        {companionEnabled && (
          <WithRecord
            render={(record) => (
              <Box sx={{ px: 3, pb: 3 }}>
                <ProofPhotos orderId={record.id} />
              </Box>
            )}
          />
        )}

        {/* ================================================================ */}
        {/* CHAT HISTORY (read-only log for completed orders — companion app only) */}
        {/* ================================================================ */}
        {companionEnabled && (
          <WithRecord
            render={(record) => (
              <Box sx={{ px: 3, pb: 3 }}>
                <ChatHistory orderId={record.id} />
              </Box>
            )}
          />
        )}

        {/* ================================================================ */}
        {/* FINANCIAL BREAKDOWN */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, pb: 3 }}>
              <GlassCard
                title="Financial Summary"
                icon={<AttachMoneyIcon />}
                iconColor={mc.gold}
              >
                <Stack spacing={1}>
                  <GlassInfoRow
                    label="Cart Subtotal"
                    value={`R${numberFormat(record.totalCart || 0, 2)}`}
                  />
                  {/* Promotions/Coupons Applied */}
                  {((record.coupons && record.coupons.length > 0) || (record.promotions && record.promotions.length > 0)) && (
                    <Box sx={{ py: 1 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                        {record.coupons?.map((coupon: any, index: number) => (
                          <Chip
                            key={`coupon-${index}`}
                            icon={<LocalOfferIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                            label={coupon.code || coupon.name || 'Coupon'}
                            size="small"
                            sx={{
                              height: 24,
                              bgcolor: `${mc.success}15`,
                              color: mc.success,
                              border: `1px solid ${mc.success}40`,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))}
                        {record.promotions?.map((promo: any, index: number) => (
                          <Chip
                            key={`promo-${index}`}
                            icon={<LocalOfferIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                            label={promo.name || promo.code || 'Promo'}
                            size="small"
                            sx={{
                              height: 24,
                              bgcolor: `${mc.secondary}15`,
                              color: mc.secondary,
                              border: `1px solid ${mc.secondary}40`,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  <GlassInfoRow
                    label="Discount"
                    value={Number(record.totalPromo) > 0 ? `-R${numberFormat(record.totalPromo, 2)}` : 'R0.00'}
                    highlight={Number(record.totalPromo) > 0}
                  />
                  <GlassInfoRow
                    label="Service Fee"
                    value={`R${numberFormat(record.totalServiceFee || 0, 2)}`}
                  />
                  <GlassInfoRow
                    label="Delivery Fee"
                    value={`R${numberFormat(record.totalDeliveryFee || 0, 2)}`}
                  />
                  <GlassInfoRow
                    label="Driver Tip"
                    value={`R${numberFormat(record.totalDriverTip || 0, 2)}`}
                  />
                  <GlassDivider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ color: mc.textPrimary, fontWeight: 700 }}>
                      TOTAL
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: mc.accent,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        textShadow: `0 0 10px ${mc.accentGlow}`,
                      }}
                    >
                      R{numberFormat(record.total, 2)}
                    </Typography>
                  </Stack>
                </Stack>
              </GlassCard>
            </Box>
          )}
        />

        {/* ================================================================ */}
        {/* STATUS PIPELINE */}
        {/* ================================================================ */}
        <WithRecord
          render={(record) => (
            <Box sx={{ px: 3, pb: 3 }}>
              <GlassCard title="Mission Phase Advance">
                <StatusPipeline
                  currentStatus={record.status || 'placed'}
                  role={role}
                  onAdvance={handleStatusAdvance}
                  saving={statusSaving}
                />
              </GlassCard>
            </Box>
          )}
        />

        {/* Action Modals */}
        <ReorderModal
          open={reorderModalOpen}
          onClose={() => setReorderModalOpen(false)}
          orderNumber={currentRecord?.number || ''}
          orderId={currentRecord?.id || ''}
          onSuccess={() => window.location.reload()}
        />
        <OrderReturnedModal
          open={returnModalOpen}
          onClose={() => setReturnModalOpen(false)}
          orderNumber={currentRecord?.number || ''}
          orderId={currentRecord?.id || ''}
          onSuccess={() => window.location.reload()}
        />
        <IssueModal
          open={issueModalOpen}
          onClose={() => setIssueModalOpen(false)}
          orderNumber={currentRecord?.number || ''}
          orderId={currentRecord?.id || ''}
          status={currentRecord?.status || ''}
          onSuccess={() => {
            // Reload order data after successful cancellation
            window.location.reload();
          }}
        />
        {/* Customer profile — same modal the Customers page uses. Carries the
            fulfilling store, which used to have its own card on this page. */}
        <CustomerDetailsModal
          open={customerModalOpen}
          onClose={() => setCustomerModalOpen(false)}
          customerId={resolveCustomerId(currentRecord)}
          originStore={
            currentRecord
              ? {
                  name: currentRecord.store?.name || currentRecord.storeId || 'Unknown Store',
                  code: currentRecord.store?.externalId || currentRecord.storeId || null,
                  streetAddress: currentRecord.store?.address?.streetAddress || null,
                  locality:
                    [currentRecord.store?.address?.suburb, currentRecord.store?.address?.city]
                      .filter(Boolean)
                      .join(', ') || null,
                  pickupNotes: currentRecord.store?.pickupNotes || null,
                  orderNumber: currentRecord.number || null,
                }
              : null
          }
        />
        {/* Proof of Delivery Modal */}
        <ProofOfDeliveryModal
          open={podModalOpen}
          onClose={() => setPodModalOpen(false)}
          orderNumber={currentRecord?.number || ''}
          orderId={currentRecord?.id || ''}
        />
        {/* Request Refund Modal */}
        <RequestRefundModal
          open={refundModalOpen}
          onClose={() => setRefundModalOpen(false)}
          orderNumber={currentRecord?.number || ''}
          orderId={currentRecord?.id || ''}
          orderTotal={currentRecord?.total || 0}
          onSuccess={() => window.location.reload()}
        />

        {/* Void Sale confirmation. Spelled out deliberately — this cannot be undone in
            Vend, so the dialog states the consequence rather than just asking. */}
        <Dialog
          open={voidConfirmOpen}
          onClose={() => !voidLoading && setVoidConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Void this sale?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This voids order <strong>{currentRecord?.number}</strong> in Vend. The sale is
              reversed and the stock goes back — Vaperite&apos;s POS will no longer record
              revenue for it.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: mc.error }}>
              This cannot be undone. Reinstating the sale means ringing up a new one.
            </Typography>
            {voidError && (
              <Typography variant="body2" sx={{ mt: 2, color: mc.error }}>
                {voidError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setVoidConfirmOpen(false)}
              disabled={voidLoading}
              sx={{ color: mc.textSecondary }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={voidLoading}
              onClick={() => currentRecord?.id && handleVoidSale(String(currentRecord.id))}
              sx={{ bgcolor: mc.error, color: 'white', '&:hover': { bgcolor: mc.error } }}
            >
              {voidLoading ? 'Voiding…' : 'Yes, void the sale'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </Edit>
  );
}
